package com.schoolconnect.app.feature.timetable

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.material3.VerticalDivider
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.AppBackground
import com.schoolconnect.app.designsystem.AppPageHeader
import com.schoolconnect.app.designsystem.AppPageSubtitle
import com.schoolconnect.app.designsystem.AppBorder
import com.schoolconnect.app.designsystem.AppCard
import com.schoolconnect.app.designsystem.AppNavy
import com.schoolconnect.app.designsystem.list.AdminListHorizontalScrollbar
import com.schoolconnect.app.feature.common.ShellPageHeader
import com.schoolconnect.app.feature.common.ShellTopRightActions
import com.schoolconnect.app.feature.common.shellAvatarInitials
import com.schoolconnect.app.feature.common.shellRoleLabel
import com.schoolconnect.app.feature.common.shellRoleSubtitle

private val TimetableGrid = Color(0xFF9CC5FF)
private val TimetableHeader = Color(0xFFE8F2FF)
private val TimetableHoliday = Color(0xFFE7EEF7)

@Composable
internal fun ParentTimeTableScreen(
    state: ParentTimeTableUiState,
    onEvent: (ParentTimeTableUiEvent) -> Unit,
    shellRoleCode: String,
    onChangeRole: () -> Unit,
    onLogout: () -> Unit
) {
    var profileExpanded by remember { mutableStateOf(false) }
    val child = state.selectedChild
    Column(
        Modifier.fillMaxSize().background(AppBackground).verticalScroll(rememberScrollState()).padding(horizontal = 24.dp, vertical = 16.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        ShellPageHeader(
            title = "Time Table",
            subtitle = "View your child's published timetable.",
            titleColor = AppPageHeader,
            subtitleColor = AppPageSubtitle,
            headerActions = {
                ShellTopRightActions(
                    roleLabel = shellRoleLabel(shellRoleCode),
                    roleSubtitle = shellRoleSubtitle(shellRoleCode),
                    initials = shellAvatarInitials(shellRoleLabel(shellRoleCode)),
                    expanded = profileExpanded,
                    onExpandedChange = { profileExpanded = it },
                    onChangeRole = onChangeRole,
                    onLogout = onLogout
                )
            }
        )

        AppCard(Modifier.fillMaxWidth(), containerColor = Color.White) {
            Row(
                Modifier.fillMaxWidth().padding(horizontal = 20.dp, vertical = 16.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                StudentInfo(label = "Student Name", value = child?.name ?: "—", modifier = Modifier.weight(1f))
                VerticalDivider(Modifier.height(52.dp), color = TimetableGrid)
                Spacer(Modifier.width(24.dp))
                StudentInfo(label = "Class / Section", value = state.classLabel?.takeIf(String::isNotBlank) ?: child?.classLabel ?: "—", modifier = Modifier.weight(1f))
            }
        }

        state.weekLabel?.let { Text("Week of $it", color = Color(0xFF6B7280), fontSize = 13.sp) }

        if (state.isLoading) {
            Box(Modifier.fillMaxWidth().padding(48.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = Color(0xFF2563EB))
            }
        } else if (state.errorMessage != null) {
            AppCard(Modifier.fillMaxWidth(), containerColor = Color.White) {
                Column(Modifier.fillMaxWidth().padding(24.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    Text(state.errorMessage, color = Color(0xFFDC2626), fontSize = 14.sp)
                    Text("Retry", color = Color(0xFF2563EB), fontSize = 14.sp, modifier = Modifier.clickable { onEvent(ParentTimeTableUiEvent.Retry) }.padding(4.dp))
                }
            }
        } else if (state.weekly.rows.isEmpty() && state.special.rows.isEmpty()) {
            AppCard(Modifier.fillMaxWidth(), containerColor = Color.White) {
                Text("No published timetable is available for this student yet.", color = Color(0xFF6B7280), fontSize = 14.sp, modifier = Modifier.padding(24.dp))
            }
        } else {

        AppCard(Modifier.fillMaxWidth(), containerColor = Color.White) {
            Column(
                Modifier.fillMaxWidth().padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(18.dp)
            ) {
                Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                    Text("Weekly Schedule", color = AppNavy, fontSize = 20.sp, fontWeight = FontWeight.SemiBold)
                    if (state.weekly.rows.isNotEmpty()) ScheduleGrid(times = state.weekly.times, rows = state.weekly.rows)
                    else Text("No regular schedule for this week.", color = Color(0xFF6B7280), fontSize = 14.sp)
                }
                if (state.special.rows.isNotEmpty()) Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                    val specialTitle = state.specialScheduleLabel?.takeIf(String::isNotBlank)?.let { label ->
                        if (label.contains(',')) "Special Schedule · $label" else "$label Schedule"
                    } ?: "Special Schedule"
                    Text(specialTitle, color = AppNavy, fontSize = 20.sp, fontWeight = FontWeight.SemiBold)
                    ScheduleGrid(times = state.special.times, rows = state.special.rows)
                }
            }
        }
        }
    }
}

@Composable
private fun StudentInfo(label: String, value: String, modifier: Modifier = Modifier) {
    Column(modifier, verticalArrangement = Arrangement.spacedBy(4.dp)) {
        Text(label, color = Color(0xFF5365C8), fontSize = 14.sp)
        Text(value, color = AppNavy, fontSize = 18.sp, fontWeight = FontWeight.Medium)
    }
}

@Composable
private fun ScheduleGrid(times: List<String>, rows: List<ParentTimeTableScheduleRow>) {
    val scroll = rememberScrollState()
    BoxWithConstraints(Modifier.fillMaxWidth()) {
        val dayColumnWidth = 92.dp
        val visiblePeriodCount = maxOf(1, times.size)
        val periodColumnWidth = maxOf((maxWidth - dayColumnWidth) / visiblePeriodCount, 180.dp)
        val contentWidth = dayColumnWidth + periodColumnWidth * visiblePeriodCount

        Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(2.dp)) {
            Box(
                Modifier.fillMaxWidth()
                    .clip(RoundedCornerShape(6.dp))
                    .border(1.dp, TimetableGrid, RoundedCornerShape(6.dp))
                    .horizontalScroll(scroll)
            ) {
                Column(Modifier.width(contentWidth)) {
                    Row(Modifier.fillMaxWidth()) {
                        ScheduleCell("Day", width = dayColumnWidth, header = true, centered = true)
                        repeat(visiblePeriodCount) { index ->
                            ScheduleCell(times.getOrElse(index) { "" }, width = periodColumnWidth, header = true, centered = true)
                        }
                    }
                    rows.forEach { row ->
                        Row(Modifier.fillMaxWidth()) {
                            ScheduleCell(row.day, width = dayColumnWidth, centered = true, holiday = row.holiday != null)
                            if (row.holiday != null) {
                                Box(
                                    Modifier.width(periodColumnWidth * visiblePeriodCount)
                                        .height(60.dp)
                                        .background(TimetableHoliday)
                                        .border(.5.dp, TimetableGrid),
                                    contentAlignment = Alignment.Center
                                ) {
                                    Text(row.holiday, color = AppNavy, fontSize = 13.sp, textAlign = TextAlign.Center)
                                }
                            } else {
                                repeat(visiblePeriodCount) { index ->
                                    ScheduleCell(row.subjects.getOrNull(index).orEmpty(), width = periodColumnWidth)
                                }
                            }
                        }
                    }
                }
            }
            AdminListHorizontalScrollbar(state = scroll, modifier = Modifier.fillMaxWidth())
        }
    }
}

@Composable
private fun androidx.compose.foundation.layout.RowScope.ScheduleCell(
    value: String,
    width: androidx.compose.ui.unit.Dp,
    header: Boolean = false,
    centered: Boolean = false,
    holiday: Boolean = false
) {
    Row(
        Modifier.width(width)
            .height(if (header) 44.dp else 60.dp)
            .background(when { header -> TimetableHeader; holiday -> TimetableHoliday; else -> Color.White })
            .border(.5.dp, TimetableGrid)
            .padding(horizontal = if (centered) 8.dp else 12.dp, vertical = if (header) 8.dp else 6.dp),
        horizontalArrangement = if (centered) Arrangement.Center else Arrangement.Start,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text(
            value,
            color = if (header) Color(0xFF1635E8) else AppNavy,
            fontSize = 13.sp,
            fontWeight = if (header) FontWeight.Medium else FontWeight.Normal,
            textAlign = if (centered) TextAlign.Center else TextAlign.Start,
            maxLines = 2
        )
    }
}
